import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// MUI Components
import MDBox from "components/MDBox";
import { Card, Divider, Tabs, Tab } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Custom Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageTitle from "examples/NewDesign/PageTitle";
import DoughnutPieChart from "components/Charts/doughnutPieChart";
import CustomAutoComplete from "components/Dropdown/CustomAutoComeplete";
import SearchableMultiSelectDropdown from "components/Dropdown/SearchableMultiSelectDropdown";
import ProjectHoursBarChart from "components/Charts/ProjectHoursBarChart";
// import ActivityHoursPivotTable from "examples/Tables/DataTable/ActivityHoursPivotTable";

// Constants
import Constants, { Colors, Icons, ButtonTitles, Common } from "utils/Constants";
import FiltersModuleName from "utils/Constants/FilterConstants.constants";

// Methods
import { capitalizeFirstLetter } from "utils/methods/methods";

// Redux
import { useDispatch, useSelector } from "react-redux";
import getProjectProgressData, {
  getReportProgressData,
  getReportStatusData,
  getShiftStatusData,
  getFilterProjects,
  getFilterReports,
  getProjectBarChartData,
  // getProjectActivityHours,
} from "redux/Thunks/projectDashboard";
import { openSnackbar } from "redux/Slice/Notification";
import {
  assetCountPerLocationThunk,
  locationListByIdThunk,
  projectStringListThunk,
  scopesThunk,
} from "redux/Thunks/FieldsData";
import { setStoreFilters, resetFilters } from "redux/Slice/Filter";

// Constants
const REPORT_SHIFT_STATUSES = ["In discussion", "Open", "Closed", "Submitted"];
const REPORT_STATUSES = ["Open", "Closed", "In discussion", "Submitted", "Checked"];
const PROJECT_MANAGEMENT_COPY = Common.PROJECT_MANAGEMENT_DASHBOARD;
const isRequestCanceled = (error) => error?.name === "AbortError" || error?.code === "ERR_CANCELED";

// Chart color configuration
const CHART_COLOR_MAP = {
  open: { backgroundColor: Colors.LIGHT_BROWN, fontColor: Colors.DARK_BROWN },
  unsafe: { backgroundColor: Colors.LIGHT_BROWN, fontColor: Colors.DARK_BROWN },
  submitted: { backgroundColor: Colors.LIGHT_ORANGE2, fontColor: Colors.DARK_ORANGE },
  incident: { backgroundColor: Colors.LIGHT_ORANGE2, fontColor: Colors.DARK_ORANGE },
  closed: { backgroundColor: Colors.LIGHT_GREEN2, fontColor: Colors.DARK_GREEN2 },
  safe: { backgroundColor: Colors.LIGHT_GREEN2, fontColor: Colors.DARK_GREEN2 },
  completed: { backgroundColor: Colors.LIGHT_GREEN2, fontColor: Colors.DARK_GREEN2 },
  in_discussion: { backgroundColor: Colors.LIGHT_YELLOW2, fontColor: Colors.DARK_YELLOW },
  "in-discussion": { backgroundColor: Colors.LIGHT_YELLOW2, fontColor: Colors.DARK_YELLOW },
  "in discussion": { backgroundColor: Colors.LIGHT_YELLOW2, fontColor: Colors.DARK_YELLOW },
  checked: { backgroundColor: Colors.LIGHT_BLUE, fontColor: Colors.DARK_BLUE },
  ncr: { backgroundColor: Colors.LIGHT_BLUE, fontColor: Colors.DARK_BLUE },
  pending: { backgroundColor: Colors.LIGHT_BLUE, fontColor: Colors.DARK_BLUE },
};

const DEFAULT_COLORS = {
  backgroundColor: Colors.LIGHT_GRAY,
  fontColor: Colors.DARK_GREY,
};

const chartColors = (title) => CHART_COLOR_MAP[title.toLowerCase()] || DEFAULT_COLORS;

// Utility functions
const createDefaultBarFilters = () => ({
  string: [],
  location: [],
  scope: [],
  asset: [],
});

const normalizeFilterArray = (values) => {
  if (!Array.isArray(values)) return [];

  const seen = new Set();
  const result = [];

  values.forEach((value) => {
    if (value == null) return;
    const stringValue = value.toString().trim();
    if (stringValue && !seen.has(stringValue)) {
      seen.add(stringValue);
      result.push(stringValue);
    }
  });

  return result;
};

const arraysEqual = (a, b) => {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

const extractDataArray = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data?.data)) return payload.data.data;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.results)) return payload.results;
  return [];
};

const formatFilterOptions = (rawOptions) => {
  const unique = new Map();

  (rawOptions || []).forEach((option, index) => {
    if (!option) return;

    const sourceOption = typeof option === "object" ? option : { value: option };
    const identifier =
      sourceOption[Constants.MONGOOSE_ID] ||
      sourceOption.id ||
      sourceOption.value ||
      `option-${index + 1}`;
    const label =
      sourceOption.title ||
      sourceOption.name ||
      sourceOption.displayName ||
      sourceOption.label ||
      identifier;

    if (!label) return;

    const key = identifier.toString();
    if (unique.has(key)) return;

    unique.set(key, {
      ...sourceOption,
      [Constants.MONGOOSE_ID]: key,
      title: label.toString(),
    });
  });

  return Array.from(unique.values()).sort((a, b) => a.title.localeCompare(b.title));
};

const getValueFromPath = (source, path) =>
  path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), source);

const getStringFromPaths = (source, paths) => {
  const match = paths
    .map((path) => {
      const candidate = getValueFromPath(source, path);
      return typeof candidate === "string" && candidate.trim() ? candidate.trim() : null;
    })
    .find(Boolean);
  return match || "";
};

const getIdFromPaths = (source, paths) => {
  if (!source || typeof source !== "object") return "";

  const match = paths
    .map((path) => {
      const candidate = getValueFromPath(source, path);
      if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
      if (typeof candidate === "number") return candidate.toString();
      return null;
    })
    .find(Boolean);
  return match || "";
};

const buildStringOptions = (strings, { locationId, scopeId, assetLocationId }) => {
  if (!Array.isArray(strings)) return [];

  return strings.reduce((acc, item, index) => {
    if (!item) return acc;

    if (typeof item === "string") {
      const normalized = item.trim();
      if (!normalized) return acc;
      acc.push({
        [Constants.MONGOOSE_ID]: normalized,
        title: normalized,
        meta: {},
      });
      return acc;
    }

    if (typeof item !== "object") return acc;

    const id =
      getIdFromPaths(item, [Constants.MONGOOSE_ID, "_id", "id", "stringId"]) ||
      getStringFromPaths(item, ["name", "title", "stringName"]) ||
      `string-${index + 1}`;
    const title =
      getStringFromPaths(item, ["name", "title", "stringName"]) || `String ${index + 1}`;

    const from =
      item.fromLocation ||
      item.from ||
      item.sourceLocation ||
      item.projectString?.fromLocation ||
      item.projectString?.from;
    const to =
      item.toLocation ||
      item.to ||
      item.destinationLocation ||
      item.projectString?.toLocation ||
      item.projectString?.to;

    const fromId = getIdFromPaths(from, [Constants.MONGOOSE_ID, "_id", "id"]);
    const toId = getIdFromPaths(to, [Constants.MONGOOSE_ID, "_id", "id"]);

    const candidateScopeEntries = [];
    const pushScopeCandidates = (scopeEntry) => {
      if (!scopeEntry) return;
      if (typeof scopeEntry === "object") {
        const scopeIdValue = getIdFromPaths(scopeEntry, [Constants.MONGOOSE_ID, "_id", "id"]);
        if (scopeIdValue) candidateScopeEntries.push(scopeIdValue);
        const scopeTitle = getStringFromPaths(scopeEntry, ["name", "title", "scopeName"]);
        if (scopeTitle) candidateScopeEntries.push(scopeTitle);
      } else if (typeof scopeEntry === "string") {
        candidateScopeEntries.push(scopeEntry);
      }
    };

    [item.scope, item.scopes, item.projectString?.scope, item.projectString?.scopes]
      .filter(Boolean)
      .forEach((scopeSource) => {
        const collection = Array.isArray(scopeSource) ? scopeSource : [scopeSource];
        collection.forEach(pushScopeCandidates);
      });

    if (item.scopeId) candidateScopeEntries.push(item.scopeId);
    if (item.scopeName) candidateScopeEntries.push(item.scopeName);

    const matchesLocation = !locationId || [fromId, toId].filter(Boolean).includes(locationId);
    const matchesAssetLocation =
      !assetLocationId || [fromId, toId].filter(Boolean).includes(assetLocationId);
    const matchesScope = !scopeId || candidateScopeEntries.includes(scopeId);

    if (!matchesLocation || !matchesAssetLocation || !matchesScope) return acc;

    acc.push({
      [Constants.MONGOOSE_ID]: id.toString(),
      title,
      meta: {
        fromId,
        toId,
        scopeCandidates: candidateScopeEntries,
      },
    });
    return acc;
  }, []);
};

const mapLocationOptions = (items = []) =>
  items.map((item, index) => {
    if (!item || typeof item !== "object") {
      return {
        [Constants.MONGOOSE_ID]: `location-${index + 1}`,
        title: `Location ${index + 1}`,
      };
    }
    const identifier =
      item[Constants.MONGOOSE_ID] ||
      item.id ||
      item.locationId ||
      item.location_id ||
      `location-${index + 1}`;
    const title =
      item.title ||
      item.name ||
      item.locationName ||
      item.displayName ||
      item.alias ||
      `Location ${index + 1}`;
    return {
      [Constants.MONGOOSE_ID]: identifier.toString(),
      title,
      raw: item,
    };
  });

const mapScopeOptions = (items = []) => {
  // Step 1: sort first
  const sortedItems = [...items].sort((a, b) => {
    const aSort = a?.sortOrder ?? Infinity; // undefined/null → last
    const bSort = b?.sortOrder ?? Infinity;
    return aSort - bSort; // ascending order
  });

  // Step 2: map after sorting
  return sortedItems.map((item, index) => {
    if (!item || typeof item !== "object") {
      return {
        [Constants.MONGOOSE_ID]: `scope-${index + 1}`,
        title: `Scope ${index + 1}`,
      };
    }
    const identifier =
      item[Constants.MONGOOSE_ID] ||
      item.scopeId ||
      item.id ||
      item.scope_id ||
      `scope-${index + 1}`;

    const title = item.scopeName || item.name || item.title || `Scope ${index + 1}`;

    return {
      [Constants.MONGOOSE_ID]: identifier.toString(),
      title,
      raw: item,
    };
  });
};

const buildAssetFilterData = (items = []) => {
  const normalizedCounts = new Set();
  const lookup = {};

  if (!Array.isArray(items) || items.length === 0) {
    return {
      options: [
        {
          [Constants.MONGOOSE_ID]: "no-assets",
          title: PROJECT_MANAGEMENT_COPY.NO_ASSETS_PLACEHOLDER,
          disabled: true,
        },
      ],
      lookup: {},
    };
  }

  items.forEach((item) => {
    if (item == null) return;

    let numericCount = null;
    let locationKeys = [];

    if (typeof item === "number" || (typeof item === "string" && item.toString().trim())) {
      const parsed = Number(item);
      if (Number.isFinite(parsed)) numericCount = parsed;
    } else if (typeof item === "object") {
      const parsed = Number(item.assetCount);
      if (Number.isFinite(parsed)) numericCount = parsed;

      const idCandidate = getIdFromPaths(item, [Constants.MONGOOSE_ID, "locationId"]);
      const nameCandidate = getStringFromPaths(item, ["locationName"]);

      locationKeys = [idCandidate, nameCandidate]
        .filter((value) => typeof value === "string" && value.trim())
        .map((value) => value.trim());
    }

    if (numericCount === null) return;

    const normalizedValue = numericCount.toString();
    normalizedCounts.add(normalizedValue);

    locationKeys.forEach((key) => {
      lookup[key] = normalizedValue;
    });
  });

  if (normalizedCounts.size === 0) {
    return {
      options: [
        {
          [Constants.MONGOOSE_ID]: "no-assets",
          title: PROJECT_MANAGEMENT_COPY.NO_ASSETS_PLACEHOLDER,
          disabled: true,
        },
      ],
      lookup: {},
    };
  }

  normalizedCounts.add("0");

  return {
    options: Array.from(normalizedCounts)
      .sort((a, b) => Number(a) - Number(b))
      .map((value) => ({
        [Constants.MONGOOSE_ID]: value,
        title: value,
      })),
    lookup,
  };
};

// Initial state generators
const createInitialProgressState = () => [
  {
    name: "Completed",
    value: 0,
    color: Colors.LIGHT_GREEN2,
    textColor: Colors.DARK_GREEN2,
  },
  {
    name: "Pending",
    value: 0,
    color: Colors.LIGHT_BLUE,
    textColor: Colors.DARK_BLUE,
  },
];

const createInitialStatusState = () => [
  {
    name: "Open",
    value: 0,
    color: Colors.LIGHT_BROWN,
    textColor: Colors.DARK_BROWN,
  },
  {
    name: "Closed",
    value: 0,
    color: Colors.LIGHT_GREEN2,
    textColor: Colors.DARK_GREEN2,
  },
  {
    name: "In discussion",
    value: 0,
    color: Colors.LIGHT_YELLOW2,
    textColor: Colors.DARK_YELLOW,
  },
  {
    name: "Submitted",
    value: 0,
    color: Colors.LIGHT_ORANGE2,
    textColor: Colors.DARK_ORANGE,
  },
  {
    name: "Checked",
    value: 0,
    color: Colors.LIGHT_BLUE,
    textColor: Colors.DARK_BLUE,
  },
];
// const formatMinutesValue = (minutes) => {
//   const total = Number(minutes);
//   if (!Number.isFinite(total) || total <= 0) {
//     return "0h 0m";
//   }
//   const safeTotal = Math.max(0, Math.round(total));
//   const hours = Math.floor(safeTotal / 60);
//   const mins = safeTotal % 60;
//   return `${hours}h ${mins}m`;
// };

function ProjectManagement() {
  const dispatch = useDispatch();
  const initializationRef = useRef(false);

  // State
  const [projectProgress, setProjectProgress] = useState(createInitialProgressState);
  const [reportProgress, setReportProgress] = useState(createInitialProgressState);
  const [reportStatus, setReportSatus] = useState(createInitialStatusState);
  const [shiftStatus, setShiftStatus] = useState(createInitialStatusState);
  const [totalCount, setTotalCount] = useState({
    projectTotalCount: 0,
    reportTotalCount: 0,
    reportStatusTotalCount: 0,
    shiftTotalCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [projectFilterLoading, setProjectFilterLoading] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [reportOptions, setReportOptions] = useState([]);
  const [selectedReport, setSelectedReport] = useState("");
  const [activeTab, setActiveTab] = useState("bar");
  const [barChartData, setBarChartData] = useState([]);
  const [barChartStatus, setBarChartStatus] = useState("idle");
  const [barChartError, setBarChartError] = useState("");
  const [barFilterOptions, setBarFilterOptions] = useState({
    string: [],
    location: [],
    scope: [],
    asset: [],
  });
  const [assetCountLookup, setAssetCountLookup] = useState({});

  const projectDashboardStoredFilters = useSelector(
    (state) => state.filtersSlice.projectManagementDashboard
  );

  const storedSelectedProject = useMemo(() => {
    const value = projectDashboardStoredFilters?.selectedProject;
    return value == null ? "" : value.toString();
  }, [projectDashboardStoredFilters]);

  const storedBarFilters = useMemo(() => {
    const defaults = createDefaultBarFilters();
    const stored = projectDashboardStoredFilters?.barFilters;

    if (!stored || typeof stored !== "object") return defaults;

    const normalized = {};
    Object.keys(defaults).forEach((key) => {
      normalized[key] = normalizeFilterArray(stored[key]);
    });
    return normalized;
  }, [projectDashboardStoredFilters]);

  const [selectedProject, setSelectedProject] = useState(storedSelectedProject);
  const [barFilters, setBarFilters] = useState(storedBarFilters);
  // const [activityHoursData, setActivityHoursData] = useState([]);
  // const [activityHoursStatus, setActivityHoursStatus] = useState("idle");
  // const [activityHoursError, setActivityHoursError] = useState("");
  const independentFiltersAbortRef = useRef(null);
  const latestIndependentFiltersRequestRef = useRef(0);
  const dashboardAbortRef = useRef(null);
  const latestDashboardRequestRef = useRef(0);
  const selectedProjectOption = useMemo(() => {
    if (!selectedProject) return null;
    const selectedId = selectedProject.toString();
    return (
      projectOptions.find((option) => option?.[Constants.MONGOOSE_ID]?.toString() === selectedId) ||
      null
    );
  }, [projectOptions, selectedProject]);

  const cloneBarFilters = useCallback((filters) => {
    const template = createDefaultBarFilters();
    const cloned = {};
    Object.keys(template).forEach((key) => {
      cloned[key] = normalizeFilterArray(filters?.[key]);
    });
    return cloned;
  }, []);

  const cancelIndependentFiltersRequest = useCallback(() => {
    if (independentFiltersAbortRef.current) {
      independentFiltersAbortRef.current.abort();
      independentFiltersAbortRef.current = null;
    }
  }, []);

  const cancelDashboardRequest = useCallback(() => {
    if (dashboardAbortRef.current) {
      dashboardAbortRef.current.abort();
      dashboardAbortRef.current = null;
    }
  }, []);

  const hydrateIndependentFilters = useCallback(
    async (projectId) => {
      cancelIndependentFiltersRequest();

      const requestId = latestIndependentFiltersRequestRef.current + 1;
      latestIndependentFiltersRequestRef.current = requestId;

      if (!projectId) {
        setBarFilterOptions({
          string: [],
          location: [],
          scope: [],
          asset: [],
        });
        setAssetCountLookup({});
        return;
      }

      const controller = new AbortController();
      independentFiltersAbortRef.current = controller;

      try {
        const [locationRes, scopeRes, stringRes, assetCountRes] = await Promise.all([
          dispatch(locationListByIdThunk({ id: projectId, signal: controller.signal })),
          dispatch(scopesThunk({ id: projectId, signal: controller.signal })),
          dispatch(projectStringListThunk({ id: projectId, signal: controller.signal })),
          dispatch(assetCountPerLocationThunk({ projectId, signal: controller.signal })),
        ]);

        if (controller.signal.aborted || latestIndependentFiltersRequestRef.current !== requestId) {
          return;
        }

        const locationOptions = mapLocationOptions(extractDataArray(locationRes?.payload));
        const scopeOptions = mapScopeOptions(extractDataArray(scopeRes?.payload));
        const stringOptions = buildStringOptions(extractDataArray(stringRes?.payload), {});
        const { options: rawAssetOptions, lookup } = buildAssetFilterData(
          extractDataArray(assetCountRes?.payload)
        );

        const assetApiFailed =
          assetCountRes?.type?.endsWith("/rejected") || assetCountRes?.payload?.status === false;

        if (assetApiFailed) {
          dispatch(
            openSnackbar({
              message: PROJECT_MANAGEMENT_COPY.ASSET_COUNT_ERROR,
              notificationType: Constants.NOTIFICATION_ERROR,
            })
          );
        }

        setAssetCountLookup(lookup);
        setBarFilterOptions({
          string: formatFilterOptions(stringOptions),
          location: formatFilterOptions(locationOptions),
          scope: scopeOptions,
          asset: rawAssetOptions,
        });
      } catch (error) {
        if (isRequestCanceled(error)) {
          return;
        }

        setAssetCountLookup({});
        setBarFilterOptions({
          string: [],
          location: [],
          scope: [],
          asset: [],
        });
      } finally {
        if (independentFiltersAbortRef.current === controller) {
          independentFiltersAbortRef.current = null;
        }
      }
    },
    [cancelIndependentFiltersRequest, dispatch]
  );

  const persistDashboardFilters = useCallback(
    (projectId, filtersState) => {
      const template = createDefaultBarFilters();
      const normalizedProjectId = projectId ? projectId.toString() : "";
      const sanitizedFilters = {};
      Object.keys(template).forEach((key) => {
        sanitizedFilters[key] = normalizeFilterArray(filtersState?.[key]);
      });

      const storedProjectId = projectDashboardStoredFilters?.selectedProject?.toString() || "";
      const storedFilters = projectDashboardStoredFilters?.barFilters || template;

      const filtersChanged = Object.keys(template).some((key) => {
        const existing = normalizeFilterArray(storedFilters[key]);
        return !arraysEqual(existing, sanitizedFilters[key]);
      });

      if (!filtersChanged && normalizedProjectId === storedProjectId) return;

      dispatch(
        setStoreFilters({
          module: "projectManagementDashboard",
          filters: {
            selectedProject: normalizedProjectId,
            barFilters: sanitizedFilters,
          },
        })
      );
    },
    [dispatch, projectDashboardStoredFilters]
  );

  // Fetch bar chart data
  useEffect(() => {
    if (!selectedProject) {
      setBarChartData([]);
      setBarChartStatus("idle");
      setBarChartError("");
      return undefined;
    }

    const controller = new AbortController();
    let isCancelled = false;

    const fetchBarChartData = async () => {
      setBarChartStatus(Constants.PENDING);
      setBarChartError("");

      try {
        const payload = await dispatch(
          getProjectBarChartData({ project: selectedProject, signal: controller.signal })
        ).unwrap();

        if (isCancelled || controller.signal.aborted) return;

        if (!payload?.status) {
          throw new Error(payload?.message || "Unable to fetch chart data");
        }

        setBarChartData(Array.isArray(payload.data) ? payload.data : []);
        setBarChartStatus("success");
      } catch (error) {
        if (isCancelled || isRequestCanceled(error)) return;

        const fallbackMessage =
          error?.message ||
          error?.data?.message ||
          "Unable to fetch chart data. Please try again later.";
        setBarChartData([]);
        setBarChartStatus("error");
        setBarChartError(fallbackMessage);
      }
    };

    fetchBarChartData();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [dispatch, selectedProject]);

  // Filter bar chart data
  // useEffect(() => {
  //   if (!selectedProject) {
  //     setActivityHoursData([]);
  //     setActivityHoursStatus("idle");
  //     setActivityHoursError("");
  //     return () => {};
  //   }

  // const controller = new AbortController();
  // let isCancelled = false;

  //   const fetchActivityHours = async () => {
  //     setActivityHoursStatus(Constants.PENDING);
  //     setActivityHoursError("");

  // try {
  //   const payload = await dispatch(
  //     getProjectActivityHours({ projectId: selectedProject, signal: controller.signal })
  //   ).unwrap();

  //   if (isCancelled || controller.signal.aborted) {
  //     return;
  //   }

  //       if (!payload?.status) {
  //         throw new Error(payload?.message || "Unable to fetch pivot table data");
  //       }

  //   setActivityHoursData(Array.isArray(payload?.data) ? payload.data : []);
  //   setActivityHoursStatus("success");
  // } catch (error) {
  //   if (isCancelled || isRequestCanceled(error)) {
  //     return;
  //   }

  //       const fallbackMessage =
  //         error?.message ||
  //         error?.data?.message ||
  //         "Failed to load pivot table data. Please try again later.";

  //       setActivityHoursData([]);
  //       setActivityHoursStatus("error");
  //       setActivityHoursError(fallbackMessage);

  //       dispatch(
  //         openSnackbar({
  //           message: "Failed to load pivot table data.",
  //           notificationType: Constants.NOTIFICATION_ERROR,
  //         })
  //       );
  //     }
  //   };

  //   fetchActivityHours();

  //   return () => {
  //     isCancelled = true;
  //     controller.abort();
  //   };
  // }, [dispatch, selectedProject]);

  const filteredBarChartResult = useMemo(() => {
    if (!Array.isArray(barChartData) || barChartData.length === 0) {
      return { data: [], zeroHoursOnly: false };
    }

    const hasSelection = (values) => normalizeFilterArray(values).length > 0;
    const normalize = (value) => (value == null ? "" : value.toString().trim().toLowerCase());

    const matchesSelection = (selectedValues, candidates) => {
      const normalizedSelected = normalizeFilterArray(selectedValues)
        .map(normalize)
        .filter((item) => item.length > 0);

      if (normalizedSelected.length === 0) return true;

      return candidates
        .map(normalize)
        .filter((candidate) => candidate.length > 0)
        .some((candidate) => normalizedSelected.includes(candidate));
    };

    const collectStringCandidates = (entry) => {
      const candidates = new Set();

      const pushCandidate = (value) => {
        if (value == null) return;

        if (typeof value === "string" || typeof value === "number") {
          const normalized = value.toString().trim();
          if (normalized) candidates.add(normalized);
          return;
        }

        if (typeof value === "object") {
          pushCandidate(value?.stringId ?? value?.[Constants.MONGOOSE_ID] ?? value?.id);
          pushCandidate(value?.stringName ?? value?.name ?? value?.title);
        }
      };

      pushCandidate(entry?.stringId);
      pushCandidate(entry?.stringName);

      const stringList = Array.isArray(entry?.strings) ? entry.strings : [];
      stringList.forEach(pushCandidate);

      return Array.from(candidates);
    };

    const collectScopeCandidates = (scopeItem) => {
      if (!scopeItem) return [];

      const candidates = new Set();

      if (typeof scopeItem === "string" || typeof scopeItem === "number") {
        const normalized = scopeItem.toString().trim();
        if (normalized) candidates.add(normalized);
        return Array.from(candidates);
      }

      if (typeof scopeItem === "object") {
        const idCandidate =
          scopeItem.scopeId || scopeItem[Constants.MONGOOSE_ID] || scopeItem.id || "";
        const nameCandidate = scopeItem.scopeName || scopeItem.name || scopeItem.title || "";

        if (idCandidate) candidates.add(idCandidate.toString().trim());
        if (nameCandidate) candidates.add(nameCandidate.toString().trim());
      }

      return Array.from(candidates).filter((candidate) => candidate.length > 0);
    };

    const hasLoggedHours = (scopeItem) => {
      if (!scopeItem) return false;
      const totalMinutes = Number(scopeItem?.totalMinutes);
      return Number.isFinite(totalMinutes) && totalMinutes > 0;
    };

    const getAssetCountCandidates = (locationEntry) => {
      const candidates = [];
      const lookupSource = assetCountLookup || {};
      const locationKey = getIdFromPaths(locationEntry, ["locationId"]);
      const locationName = getStringFromPaths(locationEntry, ["locationName", "assetLocationName"]);

      [locationKey, locationName]
        .filter((key) => typeof key === "string" && key.trim())
        .forEach((key) => {
          const lookupValue = lookupSource[key];
          if (lookupValue != null) candidates.push(lookupValue.toString());
        });

      const inlineCount = Number(
        locationEntry?.assetCount ??
          locationEntry?.assetCountPerLocation ??
          locationEntry?.assetTotal ??
          locationEntry?.numberOfAssets
      );
      if (Number.isFinite(inlineCount)) {
        candidates.push(inlineCount.toString());
      }

      return candidates;
    };

    const filteredLocations = barChartData
      .map((locationEntry) => {
        const locationMatches = matchesSelection(barFilters.location, [
          locationEntry?.locationId,
          locationEntry?.locationName,
          getIdFromPaths(locationEntry, ["locationId"]),
          getStringFromPaths(locationEntry, ["locationName", "assetLocationName"]),
        ]);

        if (!locationMatches) return null;

        const stringCandidates = collectStringCandidates(locationEntry);
        if (!matchesSelection(barFilters.string, stringCandidates)) return null;

        const assetCandidates = [
          getIdFromPaths(locationEntry, [
            "assetLocationId",
            "assetId",
            "assetLocation._id",
            "asset._id",
          ]),
          getStringFromPaths(locationEntry, [
            "assetLocationName",
            "assetName",
            "assetLocation",
            "assetTitle",
          ]),
          ...getAssetCountCandidates(locationEntry),
        ].filter(
          (candidate) => candidate != null && (typeof candidate !== "string" || candidate.trim())
        );

        if (!matchesSelection(barFilters.asset, assetCandidates)) return null;

        const scopes = Array.isArray(locationEntry?.scopes) ? locationEntry.scopes : [];
        const filteredScopes = scopes.filter((scopeItem) =>
          matchesSelection(barFilters.scope, collectScopeCandidates(scopeItem))
        );

        if (hasSelection(barFilters.scope) && filteredScopes.length === 0) return null;

        return {
          ...locationEntry,
          scopes: hasSelection(barFilters.scope) ? filteredScopes : scopes,
        };
      })
      .filter(Boolean);

    if (filteredLocations.length === 0) {
      return { data: [], zeroHoursOnly: false };
    }

    const locationsWithHours = filteredLocations
      .map((locationEntry) => {
        const scopes = Array.isArray(locationEntry?.scopes) ? locationEntry.scopes : [];
        const scopesWithHours = scopes.filter(hasLoggedHours);

        if (scopesWithHours.length === 0) return null;

        return {
          ...locationEntry,
          scopes: scopesWithHours,
        };
      })
      .filter(Boolean);

    const zeroHoursOnly = filteredLocations.length > 0 && locationsWithHours.length === 0;

    return { data: locationsWithHours, zeroHoursOnly };
  }, [assetCountLookup, barChartData, barFilters]);

  const { data: filteredBarChartData, zeroHoursOnly } = filteredBarChartResult;

  // const pivotTableData = useMemo(() => {
  //   if (!Array.isArray(activityHoursData) || activityHoursData.length === 0) {
  //     return [];
  //   }

  //   const normalizedFilters = {
  //     string: normalizeFilterArray(barFilters?.string),
  //     location: normalizeFilterArray(barFilters?.location),
  //     asset: normalizeFilterArray(barFilters?.asset),
  //     scope: normalizeFilterArray(barFilters?.scope),
  //   };

  //   const normalizeCandidate = (value) => {
  //     if (value === undefined || value === null) {
  //       return "";
  //     }
  //     return value.toString().trim().toLowerCase();
  //   };

  //   const matchesSelection = (selectedValues, candidates = []) => {
  //     const normalizedSelected = normalizeFilterArray(selectedValues)
  //       .map((item) => normalizeCandidate(item))
  //       .filter((item) => item.length > 0);

  //     if (normalizedSelected.length === 0) {
  //       return true;
  //     }

  //     return candidates
  //       .map((candidate) => normalizeCandidate(candidate))
  //       .filter((candidate) => candidate.length > 0)
  //       .some((candidate) => normalizedSelected.includes(candidate));
  //   };

  //   const lookupSource = assetCountLookup || {};

  //   const getAssetCandidates = (stringEntry, locationEntry) => {
  //     const locationKeys = [
  //       getIdFromPaths(locationEntry, [Constants.MONGOOSE_ID, "_id", "id", "locationId"]),
  //       getStringFromPaths(locationEntry, ["locationName", "name", "title"]),
  //     ]
  //       .filter((key) => typeof key === "string" && key.trim().length > 0)
  //       .map((key) => key.trim());

  //     const locationMatches = locationKeys
  //       .map((key) => lookupSource[key])
  //       .filter((value) => value !== undefined && value !== null)
  //       .map((value) => value.toString().trim());

  //     const inlineLocationCount = Number(locationEntry?.assetCount);
  //     if (Number.isFinite(inlineLocationCount)) {
  //       locationMatches.push(inlineLocationCount.toString());
  //     }

  //     const stringLevelCount = Number(stringEntry?.assetCount);
  //     if (Number.isFinite(stringLevelCount)) {
  //       locationMatches.push(stringLevelCount.toString());
  //     }

  //     return locationMatches.filter((candidate) => candidate.length > 0);
  //   };

  //   const getScopeCandidates = (member) => {
  //     const scopes = Array.isArray(member?.scopes) ? member.scopes : [];
  //     const candidates = [];

  //     scopes.forEach((scopeEntry) => {
  //       if (!scopeEntry) return;

  //       if (typeof scopeEntry === "string" || typeof scopeEntry === "number") {
  //         const normalized = scopeEntry.toString().trim();
  //         if (normalized) candidates.push(normalized);
  //         return;
  //       }

  //       if (typeof scopeEntry === "object") {
  //         const scopeIdCandidate =
  //           scopeEntry.scopeId || scopeEntry[Constants.MONGOOSE_ID] || scopeEntry.id;
  //         const scopeNameCandidate = scopeEntry.scopeName || scopeEntry.name || scopeEntry.title;

  //         if (scopeIdCandidate) {
  //           const normalized = scopeIdCandidate.toString().trim();
  //           if (normalized) candidates.push(normalized);
  //         }

  //         if (scopeNameCandidate) {
  //           const normalized = scopeNameCandidate.toString().trim();
  //           if (normalized) candidates.push(normalized);
  //         }
  //       }
  //     });

  //     return candidates;
  //   };

  //   const normalizeMembers = (members, teamKey) => {
  //     if (!Array.isArray(members)) {
  //       return [];
  //     }

  //     return members
  //       .filter((member) => matchesSelection(normalizedFilters.scope, getScopeCandidates(member)))
  //       .map((member, index) => {
  //         const safeMember = member || {};
  //         const minutes = Number(safeMember?.totalMinutes) || 0;
  //         return {
  //           ...safeMember,
  //           memberId:
  //             safeMember?.memberId ||
  //             safeMember?.userId ||
  //             safeMember?.[Constants.MONGOOSE_ID] ||
  //             `${teamKey}-member-${index + 1}`,
  //           memberName:
  //             safeMember?.memberName ||
  //             safeMember?.name ||
  //             safeMember?.title ||
  //             `Member ${index + 1}`,
  //           totalMinutes: minutes,
  //           formatted: safeMember?.formatted || formatMinutesValue(minutes),
  //         };
  //       });
  //   };

  //   return activityHoursData
  //     .map((stringEntry, stringIndex) => {
  //       const stringIdCandidate = getIdFromPaths(stringEntry, [
  //         Constants.MONGOOSE_ID,
  //         "_id",
  //         "id",
  //         "stringId",
  //       ]);
  //       const stringNameCandidate = getStringFromPaths(stringEntry, [
  //         "stringName",
  //         "name",
  //         "title",
  //       ]);
  //       const stringCandidates = [stringIdCandidate, stringNameCandidate];

  //       if (!matchesSelection(normalizedFilters.string, stringCandidates)) {
  //         return null;
  //       }

  //       const stringKey = stringIdCandidate || stringNameCandidate || `string-${stringIndex + 1}`;
  //       const stringName = stringNameCandidate || stringKey;

  //       const locations = Array.isArray(stringEntry?.locations) ? stringEntry.locations : [];

  //       const normalizedLocations = locations
  //         .map((locationEntry, locationIndex) => {
  //           const locationIdCandidate = getIdFromPaths(locationEntry, [
  //             Constants.MONGOOSE_ID,
  //             "_id",
  //             "id",
  //             "locationId",
  //           ]);
  //           const locationNameCandidate = getStringFromPaths(locationEntry, [
  //             "locationName",
  //             "name",
  //             "title",
  //           ]);
  //           const locationCandidates = [locationIdCandidate, locationNameCandidate];

  //           if (!matchesSelection(normalizedFilters.location, locationCandidates)) {
  //             return null;
  //           }

  //           if (
  //             !matchesSelection(
  //               normalizedFilters.asset,
  //               getAssetCandidates(stringEntry, locationEntry)
  //             )
  //           ) {
  //             return null;
  //           }

  //           const locationKeyBase =
  //             locationIdCandidate || locationNameCandidate || `location-${locationIndex + 1}`;
  //           const locationKey = `${stringKey}::${locationKeyBase}`;
  //           const locationName = locationNameCandidate || locationKeyBase;

  //           const teams = Array.isArray(locationEntry?.teams) ? locationEntry.teams : [];

  //           const normalizedTeams = teams
  //             .map((teamEntry, teamIndex) => {
  //               const safeTeamEntry = teamEntry || {};
  //               const teamKeyBase =
  //                 safeTeamEntry?.teamId ||
  //                 safeTeamEntry?.[Constants.MONGOOSE_ID] ||
  //                 safeTeamEntry?.teamName ||
  //                 `team-${teamIndex + 1}`;
  //               const teamKey = `${locationKey}::${teamKeyBase}`;
  //               const teamName =
  //                 safeTeamEntry?.teamName || safeTeamEntry?.name || `Team ${teamIndex + 1}`;
  //               const normalizedMembers = normalizeMembers(safeTeamEntry?.members, teamKey);
  //               const teamTotalMinutes = normalizedMembers.reduce(
  //                 (acc, member) => acc + (Number(member.totalMinutes) || 0),
  //                 0
  //               );
  //               return {
  //                 ...safeTeamEntry,
  //                 teamKey,
  //                 teamName,
  //                 members: normalizedMembers,
  //                 teamTotalMinutes,
  //                 teamFormattedTotal: formatMinutesValue(teamTotalMinutes),
  //               };
  //             })
  //             .filter((team) => team.members.length > 0);

  //           if (normalizedTeams.length === 0) {
  //             return null;
  //           }

  //           const locationTotalMinutes = normalizedTeams.reduce(
  //             (acc, team) => acc + (Number(team.teamTotalMinutes) || 0),
  //             0
  //           );

  //           return {
  //             ...locationEntry,
  //             locationKey,
  //             locationName,
  //             teams: normalizedTeams,
  //             locationTotalMinutes,
  //             locationFormattedTotal: formatMinutesValue(locationTotalMinutes),
  //           };
  //         })
  //         .filter(Boolean);

  //       if (normalizedLocations.length === 0) {
  //         return null;
  //       }

  //       const stringTotalMinutes = normalizedLocations.reduce(
  //         (acc, location) => acc + (Number(location.locationTotalMinutes) || 0),
  //         0
  //       );

  //       return {
  //         ...stringEntry,
  //         stringKey,
  //         stringId: stringIdCandidate || stringKey,
  //         stringName,
  //         locations: normalizedLocations,
  //         stringTotalMinutes,
  //         stringFormattedTotal: formatMinutesValue(stringTotalMinutes),
  //       };
  //     })
  //     .filter(Boolean);
  // }, [activityHoursData, assetCountLookup, barFilters]);

  const filterDropdownConfig = useMemo(() => {
    const normalizeOptions = (options) =>
      (Array.isArray(options) ? options : []).map((option) => {
        if (option?.[Constants.MONGOOSE_ID] != null) {
          return {
            ...option,
            [Constants.MONGOOSE_ID]: option[Constants.MONGOOSE_ID].toString(),
          };
        }
        return option;
      });

    return [
      {
        name: "string",
        label: PROJECT_MANAGEMENT_COPY.FILTER_STRING_LABEL,
        options: normalizeOptions(barFilterOptions.string),
      },
      {
        name: "location",
        label: PROJECT_MANAGEMENT_COPY.FILTER_LOCATION_LABEL,
        options: normalizeOptions(barFilterOptions.location),
      },
      {
        name: "scope",
        label: PROJECT_MANAGEMENT_COPY.FILTER_SCOPE_LABEL,
        options: normalizeOptions(barFilterOptions.scope),
      },
      {
        name: "asset",
        label: PROJECT_MANAGEMENT_COPY.FILTER_ASSET_LABEL,
        options: normalizeOptions(barFilterOptions.asset),
      },
    ];
  }, [barFilterOptions]);

  const isBarFilterDefault = useMemo(
    () => Object.values(barFilters).every((value) => normalizeFilterArray(value).length === 0),
    [barFilters]
  );

  // Sync filters with available options
  useEffect(() => {
    let updatedState = null;

    setBarFilters((previous) => {
      let nextState = previous;
      let hasChanges = false;

      Object.entries(barFilterOptions).forEach(([name, options]) => {
        const availableIds = new Set(
          (Array.isArray(options) ? options : [])
            .map((option) => option?.[Constants.MONGOOSE_ID])
            .filter((value) => value != null)
            .map((value) => value.toString())
        );

        const currentValues = normalizeFilterArray(previous[name]);
        const sanitizedValues = currentValues.filter((value) => availableIds.has(value));

        if (sanitizedValues.length !== currentValues.length) {
          if (!hasChanges) {
            nextState = { ...previous };
            hasChanges = true;
          }
          nextState[name] = sanitizedValues;
        }
      });

      if (hasChanges) {
        updatedState = nextState;
        return nextState;
      }

      return previous;
    });

    if (updatedState) {
      persistDashboardFilters(selectedProject, updatedState);
    }
  }, [barFilterOptions, persistDashboardFilters, selectedProject]);

  // Fetch dashboard data
  const projectDashboardData = useCallback(
    async (projectId) => {
      if (!projectId) return;

      cancelDashboardRequest();

      const controller = new AbortController();
      const requestId = latestDashboardRequestRef.current + 1;
      latestDashboardRequestRef.current = requestId;
      dashboardAbortRef.current = controller;

      try {
        let fetchApiDataFlag = false;
        const updatedTotalCount = {
          projectTotalCount: 0,
          reportTotalCount: 0,
          reportStatusTotalCount: 0,
          shiftTotalCount: 0,
        };

        const [projectRes, reportStatusRes, shiftStatusRes, reportListRes] = await Promise.all([
          dispatch(getProjectProgressData({ projectId, signal: controller.signal })),
          dispatch(getReportStatusData({ projectId, signal: controller.signal })),
          dispatch(getShiftStatusData({ projectId, signal: controller.signal })),
          dispatch(getFilterReports({ projectId, signal: controller.signal })),
        ]);

        if (controller.signal.aborted || latestDashboardRequestRef.current !== requestId) {
          return;
        }

        // Process report data
        if (reportListRes?.payload?.status === 200) {
          const reportOptionData = reportListRes?.payload?.data?.data || [];
          const tempReportList = reportOptionData.map((item) => ({
            [Constants.MONGOOSE_ID]: item[Constants.MONGOOSE_ID],
            title: item.title,
          }));

          setReportOptions(tempReportList);

          if (tempReportList.length > 0) {
            const currentExists = tempReportList.some(
              (r) => r[Constants.MONGOOSE_ID] === selectedReport
            );
            if (!currentExists) {
              setSelectedReport(tempReportList[0][Constants.MONGOOSE_ID]);
            }
          } else {
            setSelectedReport("");
            setReportProgress(createInitialProgressState());
          }
        } else {
          fetchApiDataFlag = true;
          setReportOptions([]);
          setSelectedReport("");
        }

        // Process project progress
        if (projectRes?.payload?.status === 200) {
          const projectData = projectRes?.payload?.data?.data || {};
          const completedProgress = projectData.reportDurations || 0;
          const totalProgress = projectData.totalReportDurations || 0;
          const pendingProgress = totalProgress - completedProgress;

          setProjectProgress([
            {
              name: "Completed",
              value: completedProgress,
              color: Colors.LIGHT_GREEN2,
              textColor: Colors.DARK_GREEN2,
            },
            {
              name: "Pending",
              value: pendingProgress,
              color: Colors.LIGHT_BLUE,
              textColor: Colors.DARK_BLUE,
            },
          ]);

          updatedTotalCount.projectTotalCount = totalProgress;
        } else {
          fetchApiDataFlag = true;
        }

        // Process report status
        if (reportStatusRes?.payload?.status === 200) {
          const reportStatusPayload = reportStatusRes.payload?.data?.data || {};
          const reportStatusCounts = reportStatusPayload?.items || [];

          const updatedReportStatusRes = REPORT_STATUSES.map((status) => {
            const found = reportStatusCounts.find((s) => s.status === status);
            const { backgroundColor, fontColor } = chartColors(status);
            return {
              name: status,
              value: found ? found.count : 0,
              color: backgroundColor,
              textColor: fontColor,
            };
          });

          setReportSatus(updatedReportStatusRes);
          updatedTotalCount.reportStatusTotalCount = reportStatusPayload?.total ?? 0;
        } else {
          fetchApiDataFlag = true;
        }

        // Process shift status
        if (shiftStatusRes?.payload?.status === 200) {
          const shiftPayload = shiftStatusRes.payload;
          const shiftCounts = shiftPayload?.data?.data?.shiftCount || [];

          const updatedShiftStatusRes = shiftCounts.map((item) => {
            const { backgroundColor, fontColor } = chartColors(item.status);
            return {
              name:
                item.status === "in_discussion"
                  ? "In discussion"
                  : capitalizeFirstLetter(item.status),
              value: item.count,
              color: backgroundColor,
              textColor: fontColor,
            };
          });

          REPORT_SHIFT_STATUSES.forEach((item) => {
            const exists = updatedShiftStatusRes.some((card) => card.name === item);
            if (!exists) {
              const { backgroundColor, fontColor } = chartColors(item);
              updatedShiftStatusRes.push({
                name: item,
                value: 0,
                color: backgroundColor,
                textColor: fontColor,
              });
            }
          });

          setShiftStatus(updatedShiftStatusRes);
          updatedTotalCount.shiftTotalCount = shiftPayload?.data?.data?.totalShiftCount ?? 0;
        } else {
          fetchApiDataFlag = true;
        }

        setTotalCount((prev) => ({
          projectTotalCount: updatedTotalCount.projectTotalCount || prev.projectTotalCount,
          reportTotalCount: updatedTotalCount.reportTotalCount || prev.reportTotalCount,
          reportStatusTotalCount: updatedTotalCount.reportStatusTotalCount,
          shiftTotalCount: updatedTotalCount.shiftTotalCount ?? prev.shiftTotalCount,
        }));

        if (latestDashboardRequestRef.current === requestId) {
          setLoading(false);
        }

        if (fetchApiDataFlag) {
          dispatch(
            openSnackbar({
              message: Constants.SOMETHING_WENT_WRONG,
              notificationType: Constants.NOTIFICATION_ERROR,
            })
          );
        }
      } finally {
        if (dashboardAbortRef.current === controller) {
          dashboardAbortRef.current = null;
        }
      }
    },
    [cancelDashboardRequest, dispatch, selectedReport]
  );

  // Initialize component
  useEffect(() => {
    if (initializationRef.current) return;
    initializationRef.current = true;

    const initializeFilters = async () => {
      setLoading(true);
      const storedSnapshot = projectDashboardStoredFilters;

      try {
        const projectListData = await dispatch(getFilterProjects({ isActive: true }));

        if (projectListData.payload.status === 200) {
          const tempProjectList = projectListData.payload.data.data.map((item) => ({
            [Constants.MONGOOSE_ID]: item[Constants.MONGOOSE_ID],
            title: item.title,
          }));

          setProjectOptions(tempProjectList);

          const availableProjectIds = tempProjectList
            .map((item) => item[Constants.MONGOOSE_ID])
            .filter((value) => value != null)
            .map((value) => value.toString());

          const defaultProjectId =
            tempProjectList.length > 0 ? tempProjectList[0][Constants.MONGOOSE_ID]?.toString() : "";
          const storedProjectId =
            storedSnapshot?.selectedProject != null
              ? storedSnapshot.selectedProject.toString()
              : "";
          const hasStoredProject = storedProjectId && availableProjectIds.includes(storedProjectId);

          const nextProjectId = hasStoredProject ? storedProjectId : defaultProjectId;
          const nextBarFilters = hasStoredProject
            ? cloneBarFilters(storedSnapshot?.barFilters)
            : createDefaultBarFilters();

          setSelectedProject(nextProjectId);
          setBarFilters(nextBarFilters);
          persistDashboardFilters(nextProjectId, nextBarFilters);

          await hydrateIndependentFilters(nextProjectId);

          if (nextProjectId) {
            await projectDashboardData(nextProjectId);
          } else {
            setLoading(false);
          }
          return;
        }

        // Handle error case
        setProjectOptions([]);
        setSelectedProject("");
        setBarFilters(createDefaultBarFilters());
        await hydrateIndependentFilters();
        dispatch(resetFilters({ module: "projectManagementDashboard" }));
        setLoading(false);
        dispatch(
          openSnackbar({
            message: Constants.SOMETHING_WENT_WRONG,
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
      } catch (error) {
        setProjectOptions([]);
        setSelectedProject("");
        setBarFilters(createDefaultBarFilters());
        await hydrateIndependentFilters();
        dispatch(resetFilters({ module: "projectManagementDashboard" }));
        setLoading(false);
        dispatch(
          openSnackbar({
            message: Constants.SOMETHING_WENT_WRONG,
            notificationType: Constants.NOTIFICATION_ERROR,
          })
        );
      }
    };

    initializeFilters();
  }, [
    cloneBarFilters,
    dispatch,
    hydrateIndependentFilters,
    persistDashboardFilters,
    projectDashboardData,
    projectDashboardStoredFilters,
  ]);

  // Event handlers
  const handleFilterChange = async (name, value) => {
    if (name !== "project") return;

    setProjectFilterLoading(true);
    const normalizedValue = value ? value.toString() : "";
    const defaultFilters = createDefaultBarFilters();

    setSelectedProject(normalizedValue);
    setBarFilters(defaultFilters);
    persistDashboardFilters(normalizedValue, defaultFilters);

    try {
      if (!normalizedValue) {
        cancelDashboardRequest();
        latestDashboardRequestRef.current += 1;
        setLoading(false);
        setProjectProgress(createInitialProgressState());
        setReportProgress(createInitialProgressState());
        setReportSatus(createInitialStatusState());
        setShiftStatus(createInitialStatusState());
        setTotalCount({
          projectTotalCount: 0,
          reportTotalCount: 0,
          reportStatusTotalCount: 0,
          shiftTotalCount: 0,
        });
        setReportOptions([]);
        setSelectedReport("");
        await hydrateIndependentFilters();
        return;
      }

      await Promise.all([
        projectDashboardData(normalizedValue),
        hydrateIndependentFilters(normalizedValue),
      ]);
    } finally {
      setProjectFilterLoading(false);
    }
  };

  // Fetch Report Progress when setup selection or project changes
  useEffect(() => {
    if (!selectedProject || !selectedReport) {
      setReportProgress(createInitialProgressState());
      setTotalCount((prev) => ({
        ...prev,
        reportTotalCount: 0,
      }));
      return undefined;
    }

    const controller = new AbortController();
    let isCancelled = false;

    const fetchReportProgress = async () => {
      try {
        const res = await dispatch(
          getReportProgressData({
            projectId: selectedProject,
            reportId: selectedReport,
            signal: controller.signal,
          })
        );

        if (isCancelled || controller.signal.aborted || res?.payload?.status !== 200) {
          return;
        }

        const payload = res.payload?.data?.data || {};
        const { completed, pending, total } = payload;

        const updatedProgress = [
          {
            name: "Completed",
            value: completed || 0,
            color: Colors.LIGHT_GREEN2,
            textColor: Colors.DARK_GREEN2,
          },
          {
            name: "Pending",
            value: pending || 0,
            color: Colors.LIGHT_BLUE,
            textColor: Colors.DARK_BLUE,
          },
        ];

        setReportProgress(updatedProgress);
        setTotalCount((prev) => ({
          ...prev,
          reportTotalCount: total || 0,
        }));
      } catch (error) {
        if (!isCancelled && !isRequestCanceled(error)) {
          setReportProgress(createInitialProgressState());
        }
      }
    };

    fetchReportProgress();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [selectedProject, selectedReport, dispatch]);

  useEffect(
    () => () => {
      cancelIndependentFiltersRequest();
      cancelDashboardRequest();
    },
    [cancelDashboardRequest, cancelIndependentFiltersRequest]
  );

  const handleReportFilterChnage = (event) => {
    setSelectedReport(event.target.value);
  };

  const handleBarFilterSelect = useCallback(
    (name, values) => {
      if (!name) return;

      const normalizedValues = normalizeFilterArray(values);

      setBarFilters((prev) => {
        const nextState = { ...prev, [name]: normalizedValues };
        persistDashboardFilters(selectedProject, nextState);
        return nextState;
      });
    },
    [persistDashboardFilters, selectedProject]
  );

  const handleResetFilters = () => {
    const defaultFilters = createDefaultBarFilters();
    setBarFilters(defaultFilters);
    persistDashboardFilters(selectedProject, defaultFilters);
  };

  const handleTabChange = (_, value) => {
    setActiveTab(value);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageTitle title="Project Management" />
      <Divider sx={{ marginTop: 2 }} />
      <MDBox sx={{ paddingBottom: 3 }}>
        {!loading ? (
          <Card sx={{ height: "fit-content" }}>
            <MDTypography
              sx={{
                fontWeight: "600",
                fontSize: pxToRem(20),
                lineHeight: pxToRem(20),
                textAlign: "center",
                backgroundColor: "#191A51",
                color: "#ffffff",
                paddingX: pxToRem(5),
                paddingY: pxToRem(5),
                borderRadius: "0.75rem 0.75rem 0 0",
              }}
            >
              Overview of Project, Reports and Shifts
            </MDTypography>
            <MDBox
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: pxToRem(5),
                paddingX: pxToRem(5),
                paddingY: pxToRem(5),
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                textColor="inherit"
                indicatorColor="primary"
                TabIndicatorProps={{
                  sx: {
                    backgroundColor: Colors.PRIMARY,
                    height: pxToRem(4),
                    borderRadius: pxToRem(4),
                  },
                }}
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: pxToRem(16),
                    color: Colors.DARK_GREY,
                    borderRadius: pxToRem(12),
                    minHeight: pxToRem(46),
                    paddingX: pxToRem(5),
                    transition: "background-color 0.2s ease, color 0.2s ease",
                  },
                  "& .Mui-selected": {
                    color: Colors.PRIMARY,
                    backgroundColor: "rgba(25, 26, 81, 0.12)",
                  },
                  "& .MuiTabs-flexContainer": {
                    gap: pxToRem(8),
                  },
                }}
              >
                <Tab
                  disableRipple
                  label={PROJECT_MANAGEMENT_COPY.TAB_ONE_LABEL || "Tab 1"}
                  value="bar"
                />
                <Tab
                  disableRipple
                  label={PROJECT_MANAGEMENT_COPY.TAB_TWO_LABEL || "Tab 2"}
                  value="donut"
                />
              </Tabs>
            </MDBox>
            {activeTab === "bar" ? (
              <MDBox
                sx={{
                  display: "grid",
                  width: "100%",
                  gridTemplateColumns: {
                    xs: `repeat(auto-fit, minmax(${pxToRem(150)}, 1fr))`,
                    sm: `repeat(auto-fit, minmax(${pxToRem(165)}, 1fr))`,
                    lg: `repeat(5, minmax(${pxToRem(185)}, 1fr)) max-content`,
                  },
                  columnGap: pxToRem(12),
                  rowGap: pxToRem(16),
                  alignItems: "flex-end",
                  paddingX: pxToRem(10),
                  paddingBottom: pxToRem(5),
                }}
              >
                <MDBox
                  sx={{
                    width: "100%",
                    minWidth: { xs: "100%", sm: pxToRem(165) },
                    maxWidth: "100%",
                  }}
                >
                  <CustomAutoComplete
                    id="filter-project"
                    name="project"
                    label={PROJECT_MANAGEMENT_COPY.PROJECT_LABEL}
                    hint={PROJECT_MANAGEMENT_COPY.PROJECT_PLACEHOLDER}
                    menu={projectOptions}
                    value={selectedProjectOption}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option?.title || ""
                    }
                    handleChange={(event) =>
                      handleFilterChange(event?.target?.name, event?.target?.value)
                    }
                    labelStyle={{ fontWeight: 600 }}
                    valueStyle={{
                      height: pxToRem(45),
                      minWidth: pxToRem(180),
                      fontWeight: 400,
                      color: "#667085",
                      backgroundColor: Colors.WHITE,
                    }}
                  />
                </MDBox>
                {filterDropdownConfig.map((config) => (
                  <MDBox
                    key={config.name}
                    sx={{
                      width: "100%",
                      minWidth: 0,
                    }}
                  >
                    <SearchableMultiSelectDropdown
                      id={`bar-filter-${config.name}`}
                      label={config.label}
                      name={config.name}
                      values={Array.isArray(barFilters[config.name]) ? barFilters[config.name] : []}
                      handleChange={handleBarFilterSelect}
                      menu={config.options}
                      hint={FiltersModuleName.ALL_WITH_FIRST_LETTER_CAPITAL}
                      showBadge
                      searchPlaceholder={`Search ${config.label}`}
                      valueStyle={{
                        backgroundColor: Colors.WHITE,
                        height: pxToRem(45),
                        verticalMarginTop: pxToRem(4),
                        menuWidth: 280,
                        menuMaxHeight: 260,
                        inputWidth: 180,
                        borderRadius: pxToRem(8),
                        paddingY: pxToRem(10),
                        paddingX: pxToRem(12),
                      }}
                      labelStyle={{
                        fontSize: pxToRem(14),
                        fontWeight: 600,
                        color: "#344054",
                      }}
                    />
                  </MDBox>
                ))}
                <MDBox
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", lg: "flex-end" },
                    justifySelf: { xs: "stretch", lg: "end" },
                  }}
                >
                  <MDButton
                    variant="outlined"
                    color="info"
                    onClick={handleResetFilters}
                    disabled={isBarFilterDefault}
                    startIcon={Icons.RESET_FILTER}
                    sx={{
                      mt: 0,
                      ml: { xs: 0, md: pxToRem(8) },
                      backgroundColor: Colors.WHITE,
                      "&:hover": {
                        backgroundColor: Colors.WHITE,
                        borderColor: Colors.PRIMARY,
                        color: Colors.PRIMARY,
                      },
                      fontSize: pxToRem(14),
                      textTransform: "capitalize",
                      height: pxToRem(45),
                      borderRadius: pxToRem(8),
                      borderColor: Colors.PRIMARY,
                      color: Colors.PRIMARY,
                      px: pxToRem(20),
                      width: { xs: "100%", md: "auto" },
                      display: "flex",
                      alignItems: "center",
                      gap: pxToRem(6),
                    }}
                  >
                    {PROJECT_MANAGEMENT_COPY.RESET_FILTERS_BUTTON}
                  </MDButton>
                </MDBox>
              </MDBox>
            ) : (
              <MDBox
                sx={{
                  paddingX: pxToRem(24),
                  paddingBottom: pxToRem(12),
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <MDBox sx={{ width: pxToRem(240) }}>
                  <CustomAutoComplete
                    id="filter-project-tab2"
                    name="project"
                    label={PROJECT_MANAGEMENT_COPY.PROJECT_LABEL}
                    hint={PROJECT_MANAGEMENT_COPY.PROJECT_PLACEHOLDER}
                    menu={projectOptions}
                    value={selectedProjectOption}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option?.title || ""
                    }
                    handleChange={(event) =>
                      handleFilterChange(event?.target?.name, event?.target?.value)
                    }
                    disabled={projectFilterLoading}
                    helperText={projectFilterLoading ? ButtonTitles.LOADING : ""}
                    labelStyle={{ fontWeight: 500 }}
                    valueStyle={{
                      height: pxToRem(45),
                      minWidth: pxToRem(220),
                      menuWidth: 360,
                      fontWeight: 400,
                      color: "#667085",
                      backgroundColor: Colors.WHITE,
                    }}
                  />
                </MDBox>
              </MDBox>
            )}
            <Divider />
            {activeTab === "bar" ? (
              <MDBox
                sx={{
                  paddingX: pxToRem(10),
                  paddingY: pxToRem(10),
                }}
              >
                <MDBox
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    gap: pxToRem(12),
                    alignItems: "stretch",
                  }}
                >
                  <MDBox
                    sx={{
                      flex: { xs: "1 1 100%", lg: "0 0 100%" },
                      maxWidth: { lg: "100%" },
                      minWidth: 0,
                      display: "flex",
                      height: "100%",
                      "& > *": {
                        width: "100%",
                        height: "100%",
                      },
                    }}
                  >
                    <ProjectHoursBarChart
                      projectOptions={projectOptions}
                      selectedProject={selectedProject}
                      onProjectChange={handleFilterChange}
                      locationScopeData={filteredBarChartData}
                      zeroHoursOnly={zeroHoursOnly}
                      loadingStatus={barChartStatus}
                      errorMessage={barChartError}
                      showProjectSelector={false}
                    />
                  </MDBox>
                  {/* <Card
                    sx={{
                      flex: { xs: "1 1 100%", lg: "0 0 30%" },
                      maxWidth: { lg: "29%" },
                      minWidth: { lg: pxToRem(320) },
                      padding: pxToRem(10),
                      boxShadow: "0px 4px 12px rgba(16, 24, 40, 0.06)",
                      border: `0px solid ${Colors.LIGHT_GRAY2}`,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <ActivityHoursPivotTable
                      data={pivotTableData}
                      status={activityHoursStatus}
                      error={activityHoursError}
                      emptyMessage="No data available for this project."
                      hasProjectSelected={Boolean(selectedProject)}
                    />
                  </Card> */}
                </MDBox>
              </MDBox>
            ) : (
              <MDBox sx={{ paddingX: pxToRem(5), paddingBottom: pxToRem(5) }}>
                {!projectFilterLoading ? (
                  <MDBox
                    sx={{
                      display: "flex",
                      justifyContent: { lg: "start", md: "center", sm: "center", xs: "center" },
                      flexDirection: "row",
                      flexWrap: "wrap",
                      alignItems: "start",
                      gap: pxToRem(20),
                      paddingTop: pxToRem(8),
                    }}
                  >
                    <MDBox
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        alignItems: "start",
                        gap: pxToRem(20),
                      }}
                    >
                      <DoughnutPieChart
                        data={projectProgress}
                        title="Project Progress"
                        totalCount={totalCount.projectTotalCount}
                        label="percentage"
                      />
                      <DoughnutPieChart
                        data={reportProgress}
                        title="Report Progress"
                        totalCount={totalCount.reportTotalCount}
                        label="percentage"
                        isapplyFilter
                        handleFilterChange={handleReportFilterChnage}
                        filterData={{
                          selectedValue: selectedReport,
                          menu: reportOptions,
                          name: "Report",
                        }}
                      />
                    </MDBox>
                    <MDBox
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        alignItems: "start",
                        gap: pxToRem(20),
                      }}
                    >
                      <DoughnutPieChart
                        data={reportStatus}
                        title="Reports by Status"
                        totalCount={totalCount.reportStatusTotalCount}
                      />
                      <DoughnutPieChart
                        data={shiftStatus}
                        title="Shifts by Status"
                        totalCount={totalCount.shiftTotalCount}
                      />
                    </MDBox>
                  </MDBox>
                ) : (
                  <MDBox
                    sx={{ height: pxToRem(400) }}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    {Icons.LOADING2}
                  </MDBox>
                )}
              </MDBox>
            )}
          </Card>
        ) : (
          <MDBox
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            {Icons.LOADING2}
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default ProjectManagement;
